import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Truck, RefreshCw, ZoomIn, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal } from "../components/Reveal";
import ParallaxImage from "../components/ParallaxImage";
import { api, PRODUCTS, BUNDLE, money } from "../lib/data";
import { useCart } from "../context/CartContext";
import WalkKitBuilder from "../components/WalkKitBuilder";
import { toast } from "sonner";

// Full-screen gallery lightbox with click-to-zoom + cursor panning.
function Lightbox({ src, onClose }) {
  const [zoom, setZoom] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-charcoal/90 p-4 backdrop-blur-sm"
      data-testid="gallery-lightbox"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        className="absolute right-5 top-5 rounded-full bg-cream/10 p-2 text-cream transition-colors hover:bg-cream/20"
      >
        <X className="h-6 w-6" />
      </button>
      <div className="overflow-hidden rounded-lg bg-white" onClick={(e) => e.stopPropagation()}>
        <img
          src={src}
          alt=""
          data-testid="lightbox-image"
          onClick={() => setZoom(!zoom)}
          onMouseMove={zoom ? move : undefined}
          onMouseLeave={() => setZoom(false)}
          style={zoom ? { transform: "scale(2)", transformOrigin: `${pos.x}% ${pos.y}%` } : undefined}
          className={`max-h-[88vh] max-w-[92vw] object-contain transition-transform duration-200 ${zoom ? "cursor-zoom-out" : "cursor-zoom-in"}`}
        />
      </div>
      <p className="absolute bottom-5 left-1/2 -translate-x-1/2 font-manrope text-xs text-cream/70">
        Click image to {zoom ? "zoom out" : "zoom in"} · Esc to close
      </p>
    </div>
  );
}

const PRODUCT_FAQ = [
  { q: "How do I choose the right size?", a: "Measure your dog's chest (or neck for the collar) at the widest point and match it to the sizing guide above. If they're between sizes, size up." },
  { q: "Is it comfortable for all-day wear?", a: "Yes — padded, breathable materials are used throughout, chosen to soften with wear rather than dig in." },
  { q: "How long does shipping take?", a: "Orders ship within 1–2 business days. Free standard shipping across Canada — no minimum purchase required." },
  { q: "What if it doesn't work out?", a: "You have 30 days to return unused gear for a refund. Personalized items are made to order." },
];

const defaultSel = (data) => {
  const init = {};
  (data?.options || []).forEach((o) => (init[o.name] = o.values[0]));
  return init;
};

// Chooses the correct product photo for the current Style + Colour selection.
const pickImage = (data, sel) => {
  if (data?.variantImages) {
    const map = data.variantImages[sel.Style] || Object.values(data.variantImages)[0];
    return (map && (map[sel.Colour] || Object.values(map)[0])) || data.gallery[0];
  }
  if (data?.colorImages && sel.Colour && data.colorImages[sel.Colour]) {
    return data.colorImages[sel.Colour];
  }
  return data?.gallery?.[0];
};

export default function Product() {
  const { slug } = useParams();
  const { add } = useCart();
  const initial = PRODUCTS.find((p) => p.slug === slug) || null;
  const [product, setProduct] = useState(initial);
  const [selected, setSelected] = useState(() => defaultSel(initial));
  const [mainImg, setMainImg] = useState(initial ? pickImage(initial, defaultSel(initial)) : "");
  const [personal, setPersonal] = useState({});
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => {
      const sel = defaultSel(r.data);
      setProduct(r.data);
      setSelected(sel);
      setMainImg(pickImage(r.data, sel));
    }).catch(() => {});
  }, [slug]);

  if (!product) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4" data-testid="product-not-found">
        <h1 className="font-fraunces text-4xl">Product not found</h1>
        <Link to="/shop" className="font-manrope underline underline-offset-4">Back to shop</Link>
      </div>
    );
  }

  const others = PRODUCTS.filter((p) => p.slug !== slug);
  const needsPersonalization = !!product.personalization;
  // Show the live engraved-nameplate preview only when the main image is a collar colour photo
  // (not the zoomed close-up) and the shopper has started typing a name.
  const showNameplate =
    needsPersonalization &&
    !!personal.pet_name &&
    Object.values(product.colorImages || {}).includes(mainImg);

  const SWATCH = {
    "Blend Orange": "linear-gradient(135deg,#E8873A 0 50%,#6FB7D4 50% 100%)",
    Orange: "#E8873A",
    Blue: "#6FB7D4",
    Pink: "#E7A6C4",
    Black: "#222220",
    Red: "#C0392B",
    Emerald: "#1E7A5A",
    Mint: "#A9D4C0",
    Teal: "#86CFD4",
    Purple: "#7A4E9E",
    Grey: "#B9BBB6",
    Brown: "#6B4A32",
    Tan: "#C9A576",
    White: "#F1EEE7",
    "Light Blue": "#A7D4E6",
    Yellow: "#D8C13A",
    Green: "#2E9E5B",
  };
  const swatchStyle = (v) =>
    (SWATCH[v] || "").startsWith("linear")
      ? { backgroundImage: SWATCH[v] }
      : { backgroundColor: SWATCH[v] || "#CBCBC4" };

  const chooseOption = (name, value) => {
    const next = { ...selected, [name]: value };
    setSelected(next);
    if (product.variantImages || product.colorImages) setMainImg(pickImage(product, next));
  };

  const colourOpt = (product.options || []).find((o) => o.name === "Colour");
  const colourMap = product.variantImages
    ? product.variantImages[selected.Style] || Object.values(product.variantImages)[0]
    : product.colorImages || null;
  const baseThumbs = colourMap && colourOpt
    ? colourOpt.values.map((c) => ({ url: colourMap[c], colour: c })).filter((t) => t.url)
    : (product.gallery || []).map((url) => ({ url }));
  const thumbs = [...baseThumbs, ...(product.extraImages || []).map((url) => ({ url }))];

  // Style-specific price (e.g. harness: Set C$39.99 vs Only C$34.99). Updates instantly on style change.
  const currentPrice =
    product.stylePrices && selected.Style && product.stylePrices[selected.Style] != null
      ? product.stylePrices[selected.Style]
      : product.price;

  const handleAdd = () => {
    const petName = (personal.pet_name || "").trim();
    const phone = (personal.phone || "").trim();
    if (needsPersonalization && (!petName || !phone)) {
      toast.error("Add your dog's name and phone number to personalize.");
      return;
    }
    const optionParts = Object.entries(selected).map(([k, v]) => `${v}`);
    if (needsPersonalization) optionParts.push(`“${petName}”`);
    add({
      slug: product.slug,
      name: product.name.split("+")[0].trim(),
      price: currentPrice,
      currency: product.currency,
      image: mainImg,
      qty: 1,
      options: needsPersonalization ? { ...selected, pet_name: petName, phone } : { ...selected },
      optionsLabel: optionParts.join(" · "),
    });
    toast.success("Added to your bag");
  };

  return (
    <div className="pt-24 md:pt-28" data-testid="product-page">
      <nav className="mx-auto max-w-[1400px] px-5 py-4 font-manrope text-sm text-ink md:px-10">
        <Link to="/" className="hover:text-rust">Home</Link> / <Link to="/shop" className="hover:text-rust">Shop</Link> /{" "}
        <span className="text-charcoal">{product.name.split("+")[0].trim()}</span>
      </nav>

      <section className="mx-auto grid max-w-[1400px] gap-10 px-5 pb-10 md:grid-cols-2 md:gap-16 md:px-10">
        {/* Gallery */}
        <div>
          <div className="relative overflow-hidden bg-white ring-1 ring-charcoal/10" data-testid="product-gallery-main">
            <motion.img
              key={mainImg}
              src={mainImg}
              alt={product.name}
              onClick={() => setLightbox(true)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="aspect-[4/5] w-full cursor-zoom-in object-contain p-6"
            />
            <button
              type="button"
              onClick={() => setLightbox(true)}
              data-testid="gallery-zoom-button"
              className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-charcoal/70 px-3 py-1.5 font-manrope text-xs font-medium text-cream backdrop-blur transition-colors hover:bg-charcoal"
            >
              <ZoomIn className="h-3.5 w-3.5" /> Zoom
            </button>
            {showNameplate && (
              <div
                data-testid="collar-name-preview"
                className="pointer-events-none absolute left-1/2 top-[56.5%] flex w-[25%] min-h-[8%] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-[3px] bg-gradient-to-b from-[#eceded] via-[#cfd1d4] to-[#a9abb0] px-1.5 py-1 text-center shadow-[0_1px_3px_rgba(0,0,0,0.4)] ring-1 ring-black/25"
              >
                <p className="truncate font-manrope text-[10px] font-bold leading-tight tracking-tight text-[#2f3133] md:text-[13px]">
                  {personal.pet_name}
                </p>
                {personal.phone && (
                  <p className="truncate font-manrope text-[7px] leading-tight text-[#54565a] md:text-[9px]">
                    {personal.phone}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="mt-4 flex gap-4">
            {thumbs.map((t, i) => (
              <button
                key={i}
                onClick={() => (t.colour ? chooseOption("Colour", t.colour) : setMainImg(t.url))}
                data-testid={`gallery-thumb-${i}`}
                className={`h-24 w-20 overflow-hidden bg-white transition-opacity ${mainImg === t.url ? "ring-2 ring-rust ring-offset-2 ring-offset-cream" : "opacity-70 ring-1 ring-charcoal/10 hover:opacity-100"}`}
              >
                <img src={t.url} alt="" className="h-full w-full object-contain p-1.5" />
              </button>
            ))}
          </div>
        </div>

        {/* Details */}
        <div className="md:sticky md:top-28 md:self-start">
          <p className="font-manrope text-xs uppercase tracking-[0.15em] font-bold text-rust">{product.tagline}</p>
          <h1 className="mt-3 font-fraunces text-4xl leading-tight md:text-5xl" data-testid="product-title">
            {product.name}
          </h1>
          <p className="mt-4 font-fraunces text-2xl" data-testid="product-price">{money(currentPrice, product.currency)}</p>
          <p className="mt-4 font-manrope text-lg text-ink">{product.short}</p>

          {/* Key benefits near top */}
          {product.benefits && (
            <ul className="mt-6 grid gap-2" data-testid="product-benefits">
              {product.benefits.slice(0, 4).map((b, i) => (
                <li key={i} className="flex items-start gap-3 font-manrope">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-forest" strokeWidth={2} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Variants */}
          <div className="mt-8 space-y-6">
            {(product.options || []).map((opt) => (
              <div key={opt.name} data-testid={`option-${opt.name.toLowerCase()}`}>
                <p className="font-manrope text-sm font-semibold">
                  {opt.name}: <span className="font-normal text-ink">{selected[opt.name]}</span>
                </p>
                {opt.name === "Colour" ? (
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    {opt.values.map((v) => (
                      <button
                        key={v}
                        type="button"
                        title={v}
                        aria-label={v}
                        aria-pressed={selected[opt.name] === v}
                        onClick={() => chooseOption("Colour", v)}
                        data-testid={`swatch-${opt.name.toLowerCase()}-${v.replace(/\s/g, "")}`}
                        className={`h-10 w-10 cursor-pointer rounded-full border border-charcoal/25 shadow-sm transition-transform duration-200 ease-out hover:scale-110 active:scale-95 ${
                          selected[opt.name] === v
                            ? "ring-2 ring-charcoal ring-offset-2 ring-offset-cream"
                            : ""
                        }`}
                        style={swatchStyle(v)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {opt.values.map((v) => (
                      <button
                        key={v}
                        onClick={() => chooseOption(opt.name, v)}
                        data-testid={`option-${opt.name.toLowerCase()}-${v.replace(/\s/g, "")}`}
                        className={`rounded-full border px-5 py-2 font-manrope text-sm transition-all duration-200 ease-out active:scale-95 ${
                          selected[opt.name] === v ? "border-charcoal bg-charcoal text-cream" : "border-charcoal/25 hover:border-charcoal"
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {needsPersonalization && (
              <div className="rounded-2xl border border-charcoal/15 bg-sand/60 p-5" data-testid="personalization">
                <p className="font-manrope text-sm font-semibold">Personalize your collar</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <input
                    placeholder="Dog's name"
                    data-testid="personalize-name"
                    value={personal.pet_name || ""}
                    onChange={(e) => setPersonal((p) => ({ ...p, pet_name: e.target.value }))}
                    className="rounded-full border border-charcoal/20 bg-cream px-4 py-3 font-manrope text-sm outline-none focus:border-rust"
                  />
                  <input
                    placeholder="Phone number"
                    data-testid="personalize-phone"
                    value={personal.phone || ""}
                    onChange={(e) => setPersonal((p) => ({ ...p, phone: e.target.value }))}
                    className="rounded-full border border-charcoal/20 bg-cream px-4 py-3 font-manrope text-sm outline-none focus:border-rust"
                  />
                </div>
                <p className="mt-2 font-manrope text-xs text-ink">Engraved directly onto the collar. Made to order.</p>
              </div>
            )}
          </div>

          <button
            onClick={handleAdd}
            data-testid="add-to-cart-button"
            className="group mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-rust px-8 py-5 font-manrope text-lg font-semibold text-cream transition-all duration-300 ease-out hover:bg-charcoal active:scale-[0.99]"
          >
            Add to bag — {money(currentPrice, product.currency)}
            <ArrowRight className="hover-arrow h-5 w-5" />
          </button>

          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 font-manrope text-sm text-ink">
            <span className="flex items-center gap-2"><Truck className="h-4 w-4" /> Ships in 1–2 days</span>
            <span className="flex items-center gap-2"><RefreshCw className="h-4 w-4" /> 30-day returns</span>
          </div>

          <WalkKitBuilder
            trigger={
              <button
                data-testid="complete-the-kit"
                className="group mt-6 flex w-full items-center justify-between gap-4 rounded-2xl border border-forest/30 bg-forest/5 p-4 text-left transition-colors hover:bg-forest/10"
              >
                <span>
                  <span className="font-manrope text-xs font-bold uppercase tracking-[0.15em] text-forest">Complete the kit &amp; save</span>
                  <span className="mt-1 block font-fraunces text-lg">
                    Get all three for {money(BUNDLE.price)}{" "}
                    <span className="text-sm text-ink line-through">{money(BUNDLE.compareAt)}</span>
                    <span data-testid="kit-prompt-save" className="ml-2 inline-block rounded-full bg-rust px-2 py-0.5 align-middle font-manrope text-xs font-bold text-cream">Save {money(BUNDLE.compareAt - BUNDLE.price)}</span>
                  </span>
                </span>
                <span className="flex-shrink-0 rounded-full bg-forest px-4 py-2 font-manrope text-sm font-semibold text-cream">Build kit</span>
              </button>
            }
          />

          {/* Accordions */}
          <Accordion type="single" collapsible className="mt-8" defaultValue="desc" data-testid="product-accordions">
            <AccordionItem value="desc">
              <AccordionTrigger className="font-manrope">Full description</AccordionTrigger>
              <AccordionContent className="font-manrope text-ink">{product.description}</AccordionContent>
            </AccordionItem>
            {product.sizing && product.sizing.rows && product.sizing.rows.length > 0 && (
              <AccordionItem value="sizing">
                <AccordionTrigger className="font-manrope">Sizing guide</AccordionTrigger>
                <AccordionContent>
                  <table className="w-full font-manrope text-sm" data-testid="sizing-table">
                    <thead>
                      <tr className="border-b border-charcoal/15 text-left text-ink">
                        {product.sizing.columns.map((c) => (
                          <th key={c} className="py-2 font-semibold">{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {product.sizing.rows.map((row) => (
                        <tr key={row[0]} className="border-b border-charcoal/10">
                          {row.map((cell, ci) => (
                            <td key={ci} className={`py-2 ${ci === 0 ? "font-semibold" : "text-ink"}`}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </AccordionContent>
              </AccordionItem>
            )}
            <AccordionItem value="ship">
              <AccordionTrigger className="font-manrope">Shipping & returns</AccordionTrigger>
              <AccordionContent className="font-manrope text-ink">
                Orders ship within 1–2 business days. Free standard shipping across Canada — no minimum purchase required. Returns accepted within 30 days on unused items. Personalized collars are made to order and non-returnable unless faulty.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {product.lifestyleImage && (
        <section className="relative mt-8 h-[40vh] w-full overflow-hidden md:h-[58vh]" data-testid="product-lifestyle">
          <ParallaxImage src={product.lifestyleImage} alt={`A happy dog — ${product.name}`} className="lifestyle-img" focus={product.lifestyleFocus || "center"} />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/65 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 p-8 md:p-14">
            <p className="font-manrope text-xs uppercase tracking-[0.15em] font-bold text-cream/85">{product.tagline}</p>
            <h2 className="mt-3 max-w-xl font-fraunces text-3xl font-light leading-tight text-cream md:text-5xl">
              Made for the miles you share.
            </h2>
          </div>
        </section>
      )}

      {/* Product FAQ */}
      <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10" data-testid="product-faq">
        <div className="grid gap-10 md:grid-cols-12">
          <h2 className="font-fraunces text-3xl md:col-span-4 md:text-4xl">Questions, answered.</h2>
          <div className="md:col-span-7 md:col-start-6">
            <Accordion type="single" collapsible>
              {PRODUCT_FAQ.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`}>
                  <AccordionTrigger className="text-left font-manrope text-lg">{f.q}</AccordionTrigger>
                  <AccordionContent className="font-manrope text-ink">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Complementary */}
      <section className="border-t border-charcoal/10 bg-sand" data-testid="complementary-products">
        <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10">
          <h2 className="mb-10 font-fraunces text-3xl md:text-4xl">Pairs well with</h2>
          <div className="grid gap-8 sm:grid-cols-2">
            {others.map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="group block" data-testid={`complementary-${p.slug}`}>
                <div className="aspect-[16/10] overflow-hidden bg-white ring-1 ring-charcoal/10">
                  <img src={p.gallery[0]} alt={p.name} className="img-zoom h-full w-full object-contain p-4" />
                </div>
                <div className="mt-4 flex items-start justify-between gap-3">
                  <div>
                    <p className="font-manrope text-xs uppercase tracking-[0.15em] text-rust">{p.tagline}</p>
                    <h3 className="mt-1 font-fraunces text-2xl">{p.name.split("+")[0].trim()}</h3>
                  </div>
                  <span className="font-manrope font-semibold">{money(p.price, p.currency)}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {lightbox && <Lightbox src={mainImg} onClose={() => setLightbox(false)} />}
    </div>
  );
}
